/**
 * client/render/fx.ts — particle stand-ins for the BO2 fx (the fx assets are names only: OAT v0.33 has no
 * T6 fx dumper, research/weapons-mp.md §7.3). OWNER: RENDER agent.
 *
 * One instanced billboard mesh draws every particle in ONE call: premultiplied blending (src ONE,
 * dst 1-srcAlpha) lets additive particles (alpha 0) and smoke/blood (alpha > 0) share the pass; particles are
 * depth-sorted back to front each frame. Colours are display-space (they go through the LUT like the world).
 * Every emitter below names the BO2 fx it stands in for; sizes/lifetimes/colours are ESTIMATE (by eye vs
 * gameplay footage memory), the triggers and positions are from the game events.
 */
import * as THREE from 'three';

export const Tile = {
  GLOW: 0, SMOKE: 1, SMOKE_B: 2, SMOKE_C: 3, CHIP: 4, BLOOD: 5, FIRE: 6, FIRE_B: 7, DEBRIS: 8, DEBRIS_B: 9, SPARK: 10,
} as const;
export type Tile = (typeof Tile)[keyof typeof Tile];
/** Atlas grid: ATLAS_N x ATLAS_N tiles of TILE_PX. */
export const ATLAS_N = 4;
const TILE_PX = 128;

export interface Particle {
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
  age: number; life: number;
  /** Seconds before the particle appears (it waits at its spawn point). */
  delay: number;
  size0: number; size1: number;
  /** Colour at birth (r,g,b) lerps to (r1,g1,b1) at death. */
  r: number; g: number; b: number;
  r1: number; g1: number; b1: number;
  a0: number; a1: number;
  /** Fraction of the life spent fading in from 0 to a0 (0 = pops in). */
  fadeIn: number;
  additive: boolean;
  tile: Tile;
  rot: number; spin: number;
  gravity: number; drag: number;
  stretch: number; // >0: streak along velocity, length = speed * stretch
  /** Buoyancy (in/s² up) for hot smoke. */
  lift: number;
}

const MAX = 3000;

/**
 * Camera-proximity fade (pure, mirrored in the vertex shader): particles fade out as the camera gets inside
 * them, the usual CoD fx "fade near camera" behaviour (ESTIMATE ranges). Without it, a debris chunk or a
 * smoke puff passing the lens fills the screen as a flat quad (verify-look 11-axe-0126).
 */
export function nearFade(distToCamera: number, size: number): number {
  const ss = (a: number, b: number, x: number) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
  return ss(4, 24, distToCamera) * ss(0.25, 0.8, distToCamera / Math.max(size, 1));
}

/** Alpha of a particle at normalised age t in [0,1] (fade in over `fadeIn`, then a0 -> a1). Pure. */
export function particleAlpha(t: number, a0: number, a1: number, fadeIn: number): number {
  const base = a0 + (a1 - a0) * t;
  return fadeIn > 0 && t < fadeIn ? base * (t / fadeIn) : base;
}

const VERT = /* glsl */ `
attribute vec4 iPosSize;   // xyz, size
attribute vec4 iColor;     // rgb (display space), alpha (0 = additive)
attribute vec4 iMisc;      // rot, tile, stretch, additive flag
attribute vec3 iVel;
varying vec2 vUv;
varying vec4 vColor;
varying float vAdd;
varying float vShade;
float ss(float a, float b, float x) { float t = clamp((x - a) / (b - a), 0.0, 1.0); return t * t * (3.0 - 2.0 * t); }
void main() {
  vec3 camRight = vec3(viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0]);
  vec3 camUp = vec3(viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1]);
  vec3 p = iPosSize.xyz;
  float s = iPosSize.w;
  vec2 c = position.xy; // -0.5..0.5
  vec3 wp;
  float up;
  if (iMisc.z > 0.0 && dot(iVel, iVel) > 1.0) {
    vec3 vdir = normalize(iVel);
    vec3 toCam = normalize(cameraPosition - p);
    vec3 side = normalize(cross(vdir, toCam));
    float len = max(s, length(iVel) * iMisc.z);
    wp = p + side * c.x * s + vdir * (c.y - 0.5) * len;
    up = c.y + 0.5;
  } else {
    float cr = cos(iMisc.x), sr = sin(iMisc.x);
    vec2 r = vec2(c.x * cr - c.y * sr, c.x * sr + c.y * cr);
    wp = p + (camRight * r.x + camUp * r.y) * s;
    // screen-up corner coordinate (0 bottom .. 1 top): smoke is lit from above (ESTIMATE top/bottom 1.12/0.7)
    up = clamp(dot(vec2(c.x, c.y), vec2(-sr, cr)) + 0.5, 0.0, 1.0);
  }
  float tile = iMisc.y;
  vUv = (uv + vec2(mod(tile, ${ATLAS_N}.0), floor(tile / ${ATLAS_N}.0))) * (1.0 / ${ATLAS_N}.0);
  // near-camera fade (nearFade() in fx.ts)
  float dcam = length(cameraPosition - p);
  float fade = ss(4.0, 24.0, dcam) * ss(0.25, 0.8, dcam / max(s, 1.0));
  vColor = vec4(iColor.rgb, iColor.a * fade);
  vAdd = iMisc.w;
  vShade = mix(0.7, 1.12, up);
  gl_Position = projectionMatrix * viewMatrix * vec4(wp, 1.0);
}
`;
const FRAG = /* glsl */ `
uniform sampler2D uAtlas;
varying vec2 vUv;
varying vec4 vColor;
varying float vAdd;
varying float vShade;
void main() {
  vec4 t = texture2D(uAtlas, vUv);
  float a = t.a * vColor.a;
  if (vAdd > 0.5) {
    gl_FragColor = vec4(vColor.rgb * t.rgb * a, 0.0); // additive through premultiplied blending
  } else {
    gl_FragColor = vec4(vColor.rgb * t.rgb * vShade * a, a);
  }
}
`;

// ---- procedural atlas -------------------------------------------------------------------------
function hash2(ix: number, iy: number, seed: number): number {
  let h = Math.imul(ix, 374761393) ^ Math.imul(iy, 668265263) ^ Math.imul(seed, 2147483647);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}
function vnoise(x: number, y: number, seed: number): number {
  const ix = Math.floor(x), iy = Math.floor(y);
  const fx = x - ix, fy = y - iy;
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy);
  const a = hash2(ix, iy, seed), b = hash2(ix + 1, iy, seed), c = hash2(ix, iy + 1, seed), d = hash2(ix + 1, iy + 1, seed);
  return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
}
/** Fractal value noise in [0,1]. Pure (exported for tests). */
export function fbm(x: number, y: number, seed: number, octaves = 5): number {
  let s = 0, amp = 0.5, f = 1, norm = 0;
  for (let i = 0; i < octaves; i++) { s += amp * vnoise(x * f, y * f, seed + i * 17); norm += amp; amp *= 0.5; f *= 2.03; }
  return s / norm;
}
const sstep = (a: number, b: number, x: number) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

/** RGBA pixels of one atlas tile (pure; unit tested for coverage/variation). */
export function tilePixels(tile: Tile, S = TILE_PX): Uint8ClampedArray<ArrayBuffer> {
  const px = new Uint8ClampedArray(S * S * 4);
  const put = (i: number, v: number, a: number) => { px[i] = px[i + 1] = px[i + 2] = Math.round(v * 255); px[i + 3] = Math.round(a * 255); };
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const u = ((x + 0.5) / S) * 2 - 1, v = ((y + 0.5) / S) * 2 - 1;
      const r = Math.hypot(u, v);
      const i = (y * S + x) * 4;
      switch (tile) {
        case Tile.GLOW: {
          const a = Math.max(0, 1 - r);
          put(i, 1, Math.min(1, a * a * 1.4));
          break;
        }
        case Tile.SMOKE: case Tile.SMOKE_B: case Tile.SMOKE_C: {
          // billowy puff: noisy edge + internal density/brightness variation (no flat disc)
          const seed = 11 + tile * 7;
          const n = fbm(u * 2.2 + 3, v * 2.2 + 5, seed);
          const n2 = fbm(u * 4.5 + 9, v * 4.5 + 1, seed + 3);
          const edge = r + (n - 0.5) * 0.75;
          const mask = 1 - sstep(0.35, 0.92, edge);
          const dens = mask * (0.45 + 0.75 * n2);
          put(i, 0.62 + 0.38 * sstep(0.25, 0.8, n2), Math.min(1, dens) * (1 - sstep(0.9, 1.0, r)));
          break;
        }
        case Tile.FIRE: case Tile.FIRE_B: {
          const seed = 101 + tile * 13;
          const n = fbm(u * 3 + 1, v * 3 + 7, seed);
          const ridge = 1 - Math.abs(2 * fbm(u * 5 + 2, v * 5 + 4, seed + 5) - 1);
          const mask = 1 - sstep(0.25, 0.95, r + (n - 0.5) * 0.7);
          const it = mask * (0.35 + 0.95 * ridge * ridge);
          put(i, Math.min(1, it), Math.min(1, it * 1.2) * (1 - sstep(0.9, 1.0, r)));
          break;
        }
        case Tile.SPARK: {
          const a = Math.exp(-((u * 3.2) ** 2)) * Math.max(0, 1 - Math.abs(v)) ** 0.5;
          put(i, 1, a);
          break;
        }
        default: break; // vector tiles are drawn with canvas paths
      }
    }
  }
  return px;
}

/** Irregular chunk outline (debris), deterministic. */
function chunkPoly(seed: number, n: number): [number, number][] {
  const pts: [number, number][] = [];
  for (let k = 0; k < n; k++) {
    const ang = (k / n) * Math.PI * 2 + (hash2(k, 1, seed) - 0.5) * 0.6;
    const rad = 0.45 + 0.4 * hash2(k, 2, seed);
    pts.push([Math.cos(ang) * rad, Math.sin(ang) * rad]);
  }
  return pts;
}

function makeAtlas(): THREE.Texture {
  const S = TILE_PX, N = ATLAS_N;
  const c = document.createElement('canvas');
  c.width = c.height = S * N;
  const g = c.getContext('2d')!;
  const origin = (t: number): [number, number] => [(t % N) * S, Math.floor(t / N) * S];
  for (const t of [Tile.GLOW, Tile.SMOKE, Tile.SMOKE_B, Tile.SMOKE_C, Tile.FIRE, Tile.FIRE_B, Tile.SPARK] as Tile[]) {
    const [ox, oy] = origin(t);
    g.putImageData(new ImageData(tilePixels(t, S), S, S), ox, oy);
  }
  // chip (small hard-edged shard, impacts)
  {
    const [ox, oy] = origin(Tile.CHIP);
    g.fillStyle = 'rgba(255,255,255,1)';
    g.beginPath(); g.moveTo(ox + S * 0.3, oy + S * 0.2); g.lineTo(ox + S * 0.8, oy + S * 0.35); g.lineTo(ox + S * 0.65, oy + S * 0.8); g.lineTo(ox + S * 0.2, oy + S * 0.6); g.closePath(); g.fill();
  }
  // debris chunks: irregular, shaded (light top-left, dark rim) so they read as solid bits, not flat squares
  for (const [t, seed, n] of [[Tile.DEBRIS, 3, 7], [Tile.DEBRIS_B, 9, 6]] as [Tile, number, number][]) {
    const [ox, oy] = origin(t);
    const pts = chunkPoly(seed, n);
    const grad = g.createLinearGradient(ox + S * 0.2, oy + S * 0.2, ox + S * 0.8, oy + S * 0.8);
    grad.addColorStop(0, 'rgba(255,255,255,1)'); grad.addColorStop(1, 'rgba(120,120,120,1)');
    g.fillStyle = grad;
    g.strokeStyle = 'rgba(70,70,70,1)'; g.lineWidth = S * 0.05;
    g.beginPath();
    pts.forEach(([px, py], k) => { const X = ox + S / 2 + px * S * 0.5, Y = oy + S / 2 + py * S * 0.5; if (k) g.lineTo(X, Y); else g.moveTo(X, Y); });
    g.closePath(); g.fill(); g.stroke();
  }
  // blood splat
  let seed = 7;
  const rnd0 = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
  {
    const [ox, oy] = origin(Tile.BLOOD);
    for (let i = 0; i < 18; i++) {
      const x = ox + S / 2 + (rnd0() - 0.5) * S * 0.5, y = oy + S / 2 + (rnd0() - 0.5) * S * 0.5, r = S * (0.05 + rnd0() * 0.16);
      const grad = g.createRadialGradient(x, y, 0, x, y, r);
      grad.addColorStop(0, 'rgba(255,255,255,0.9)');
      grad.addColorStop(0.7, 'rgba(255,255,255,0.6)');
      grad.addColorStop(1, 'rgba(255,255,255,0)');
      g.fillStyle = grad; g.fillRect(ox, oy, S, S);
    }
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.NoColorSpace;
  t.premultiplyAlpha = false;
  t.flipY = false; // canvas row 0 = v 0, matching the tile math in the vertex shader
  t.generateMipmaps = true;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  return t;
}

let rngState = 0x1234567;
function rnd(): number { rngState = (Math.imul(rngState, 1103515245) + 12345) >>> 0; return (rngState >>> 8) / 16777216; }
function rr(a: number, b: number): number { return a + (b - a) * rnd(); }

/** surfaceType -> dust colour (display space) for impacts. */
function surfaceDust(surface: number): [number, number, number] {
  switch (surface) {
    case 21: return [0.45, 0.32, 0.2]; //  wood
    case 13: case 28: return [0.55, 0.55, 0.55]; // metal / paintedmetal
    case 9: return [0.85, 0.9, 0.95]; //   glass
    case 3: case 4: case 26: return [0.6, 0.55, 0.5]; // carpet / cloth / cushion
    default: return [0.7, 0.66, 0.6];
  }
}

export class FxSystem {
  readonly mesh: THREE.Mesh;
  private readonly parts: Particle[] = [];
  private readonly geo: THREE.InstancedBufferGeometry;
  private readonly aPos: THREE.InstancedBufferAttribute;
  private readonly aCol: THREE.InstancedBufferAttribute;
  private readonly aMisc: THREE.InstancedBufferAttribute;
  private readonly aVel: THREE.InstancedBufferAttribute;
  private readonly order: number[] = [];
  private readonly depth = new Float32Array(MAX);
  /** Dynamic light requests: the strongest live one wins (renderer feeds Bo2Uniforms.uDynLight*). */
  readonly lights: { pos: THREE.Vector3; color: THREE.Vector3; radius: number; age: number; life: number }[] = [];
  spawned = 0;

  constructor() {
    const base = new THREE.PlaneGeometry(1, 1);
    this.geo = new THREE.InstancedBufferGeometry();
    this.geo.index = base.index;
    this.geo.setAttribute('position', base.attributes.position);
    this.geo.setAttribute('uv', base.attributes.uv);
    this.aPos = new THREE.InstancedBufferAttribute(new Float32Array(MAX * 4), 4);
    this.aCol = new THREE.InstancedBufferAttribute(new Float32Array(MAX * 4), 4);
    this.aMisc = new THREE.InstancedBufferAttribute(new Float32Array(MAX * 4), 4);
    this.aVel = new THREE.InstancedBufferAttribute(new Float32Array(MAX * 3), 3);
    for (const a of [this.aPos, this.aCol, this.aMisc, this.aVel]) a.setUsage(THREE.DynamicDrawUsage);
    this.geo.setAttribute('iPosSize', this.aPos);
    this.geo.setAttribute('iColor', this.aCol);
    this.geo.setAttribute('iMisc', this.aMisc);
    this.geo.setAttribute('iVel', this.aVel);
    this.geo.instanceCount = 0;
    const mat = new THREE.ShaderMaterial({
      uniforms: { uAtlas: { value: typeof document !== 'undefined' ? makeAtlas() : null } }, // null: unit tests (node)
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      blending: THREE.CustomBlending,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneMinusSrcAlphaFactor,
      side: THREE.DoubleSide,
    });
    this.mesh = new THREE.Mesh(this.geo, mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 100;
    this.mesh.name = 'bo2_fx';
  }

  get count(): number { return this.parts.length; }
  /** The particle atlas (null in node unit tests). */
  get atlas(): THREE.Texture | null { return (this.mesh.material as THREE.ShaderMaterial).uniforms.uAtlas.value as THREE.Texture | null; }

  /** Live particles (read-only view, for tests and stats). */
  get particles(): readonly Particle[] { return this.parts; }

  private add(p: Partial<Particle> & { x: number; y: number; z: number }): void {
    if (this.parts.length >= MAX) this.parts.shift();
    this.spawned++;
    const q: Particle = {
      vx: 0, vy: 0, vz: 0, age: 0, life: 1, delay: 0, size0: 4, size1: 4, r: 1, g: 1, b: 1, r1: -1, g1: -1, b1: -1,
      a0: 1, a1: 0, fadeIn: 0, additive: false, tile: Tile.GLOW, rot: rnd() * 6.283, spin: 0, gravity: 0, drag: 0, stretch: 0, lift: 0, ...p,
    };
    if (q.r1 < 0) { q.r1 = q.r; q.g1 = q.g; q.b1 = q.b; }
    this.parts.push(q);
  }

  light(pos: THREE.Vector3, color: [number, number, number], radius: number, life: number): void {
    this.lights.push({ pos: pos.clone(), color: new THREE.Vector3(...color), radius, age: 0, life });
  }

  // ---- emitters (three-space positions / directions) ------------------------------------------

  /**
   * explosive_bolt_mp detonation: projExplosionType grenade [WPN §2.2], so it stands in for the BO2 grenade
   * explosion fx (the fx file itself is not dumped, weapons-mp.md §7.3). Layers, all ESTIMATE by eye against
   * BO2 gameplay: a white-hot flash, a turbulent fireball that cools from yellow to deep orange, soot that
   * boils out of the fireball, a grey dust/smoke plume that rises and spreads for ~3 s, a ring of deck dust
   * pushed out along the ground, sparks and embers, and small dark debris chunks. Fireball sized to the
   * blast (explosionRadius 100 [WPN §2.2]).
   */
  explosion(p: THREE.Vector3): void {
    const smokeTile = () => (Tile.SMOKE + Math.floor(rnd() * 3)) as Tile;
    const v = new THREE.Vector3();
    const dir = (minUp: number, maxUp: number, speed0: number, speed1: number) =>
      v.set(rr(-1, 1), rr(minUp, maxUp), rr(-1, 1)).normalize().multiplyScalar(rr(speed0, speed1));
    // flash core
    this.add({ x: p.x, y: p.y + 14, z: p.z, life: 0.09, size0: 120, size1: 190, r: 1, g: 0.93, b: 0.78, a0: 1, a1: 0, additive: true });
    // fireball: turbulent flame sprites, cooling yellow-white -> orange -> dark red
    for (let i = 0; i < 12; i++) {
      dir(-0.1, 1, 120, 340);
      this.add({
        x: p.x + rr(-10, 10), y: p.y + rr(8, 22), z: p.z + rr(-10, 10), vx: v.x, vy: v.y + 40, vz: v.z, drag: 7, delay: rr(0, 0.04),
        life: rr(0.28, 0.5), size0: rr(28, 44), size1: rr(85, 130), r: 1, g: rr(0.78, 0.9), b: rr(0.45, 0.6), r1: 0.75, g1: 0.2, b1: 0.04,
        a0: 1, a1: 0, additive: true, tile: rnd() < 0.5 ? Tile.FIRE : Tile.FIRE_B, spin: rr(-1.5, 1.5),
      });
    }
    // soot boiling out of the fireball
    for (let i = 0; i < 8; i++) {
      dir(0, 1, 60, 160);
      const k = rr(0.12, 0.2);
      this.add({
        x: p.x + rr(-15, 15), y: p.y + rr(20, 40), z: p.z + rr(-15, 15), vx: v.x, vy: v.y, vz: v.z, drag: 2.5, lift: 70, delay: rr(0.1, 0.22),
        life: rr(0.9, 1.4), size0: rr(45, 60), size1: rr(130, 180), r: k, g: k * 0.93, b: k * 0.86, r1: 0.3, g1: 0.29, b1: 0.27,
        a0: 0.75, a1: 0, fadeIn: 0.08, tile: smokeTile(), spin: rr(-0.4, 0.4),
      });
    }
    // lingering grey dust/smoke plume
    for (let i = 0; i < 10; i++) {
      dir(0.1, 1, 30, 90);
      const k = rr(0.56, 0.7);
      this.add({
        x: p.x + rr(-25, 25), y: p.y + rr(15, 50), z: p.z + rr(-25, 25), vx: v.x, vy: v.y + 25, vz: v.z, drag: 0.9, lift: 18, delay: rr(0.15, 0.4),
        life: rr(2.4, 3.6), size0: rr(60, 90), size1: rr(190, 260), r: k, g: k * 0.97, b: k * 0.92,
        a0: rr(0.32, 0.45), a1: 0, fadeIn: 0.15, tile: smokeTile(), spin: rr(-0.25, 0.25),
      });
    }
    // dust ring pushed out along the deck
    for (let i = 0; i < 14; i++) {
      const a = (i / 14) * Math.PI * 2 + rr(-0.2, 0.2), sp = rr(260, 460);
      const k = rr(0.55, 0.66);
      this.add({
        x: p.x, y: p.y + rr(4, 12), z: p.z, vx: Math.cos(a) * sp, vy: rr(10, 40), vz: Math.sin(a) * sp, drag: 3.2, delay: rr(0, 0.05),
        life: rr(0.9, 1.5), size0: rr(18, 26), size1: rr(70, 100), r: k, g: k * 0.93, b: k * 0.84,
        a0: 0.45, a1: 0, fadeIn: 0.05, tile: smokeTile(), spin: rr(-0.6, 0.6),
      });
    }
    // sparks (fast streaks) and embers (slow glowing bits)
    for (let i = 0; i < 32; i++) {
      dir(-0.05, 1, 400, 1100);
      this.add({ x: p.x, y: p.y + 6, z: p.z, vx: v.x, vy: v.y, vz: v.z, gravity: 800, drag: 0.6, life: rr(0.3, 0.8), size0: 1.4, size1: 0.7, r: 1, g: rr(0.75, 0.9), b: rr(0.35, 0.5), r1: 1, g1: 0.4, b1: 0.1, a0: 1, a1: 0.2, additive: true, tile: Tile.SPARK, stretch: 0.025 });
    }
    for (let i = 0; i < 10; i++) {
      dir(0.2, 1, 80, 260);
      this.add({ x: p.x + rr(-10, 10), y: p.y + rr(10, 30), z: p.z + rr(-10, 10), vx: v.x, vy: v.y, vz: v.z, gravity: 250, drag: 1.2, life: rr(0.9, 1.6), size0: 1.6, size1: 0.8, r: 1, g: 0.55, b: 0.15, r1: 0.7, g1: 0.15, b1: 0.03, a0: 1, a1: 0, additive: true });
    }
    // small dark debris chunks (shaded tiles, a few inches across)
    for (let i = 0; i < 14; i++) {
      dir(0.15, 1, 220, 560);
      const k = rr(0.16, 0.3);
      this.add({ x: p.x, y: p.y + 4, z: p.z, vx: v.x, vy: v.y, vz: v.z, gravity: 800, life: rr(0.9, 1.4), size0: rr(1.2, 2.8), size1: rr(1.2, 2.8), r: k, g: k * 0.9, b: k * 0.8, a0: 1, a1: 0.6, tile: rnd() < 0.5 ? Tile.DEBRIS : Tile.DEBRIS_B, spin: rr(-14, 14) });
    }
    // engine dynamic light on explosions (ESTIMATE colour/radius), HDR scene units
    this.light(p.clone().setY(p.y + 20), [24, 12, 5], 420, 0.3);
  }

  /** prj_bolt_impact / knife / bounce impact puffs (impact fx by surface, names not dumped). */
  impact(p: THREE.Vector3, n: THREE.Vector3, surface: number, scale = 1): void {
    if (surface === 20) { this.splash(p, 0.4 * scale); return; }
    const [r, g, b] = surfaceDust(surface);
    for (let i = 0; i < 5; i++) {
      const d = n.clone().multiplyScalar(rr(30, 80)).add(new THREE.Vector3(rr(-25, 25), rr(-10, 25), rr(-25, 25)));
      this.add({ x: p.x, y: p.y, z: p.z, vx: d.x * scale, vy: d.y * scale, vz: d.z * scale, drag: 3, life: rr(0.5, 0.9), size0: 6 * scale, size1: rr(22, 34) * scale, r, g, b, a0: 0.55, a1: 0, tile: (Tile.SMOKE + (i % 3)) as Tile });
    }
    const metal = surface === 13 || surface === 28;
    const n2 = metal ? 10 : surface === 9 ? 8 : 5;
    for (let i = 0; i < n2; i++) {
      const d = n.clone().multiplyScalar(rr(80, 260)).add(new THREE.Vector3(rr(-120, 120), rr(-40, 120), rr(-120, 120)));
      if (metal) this.add({ x: p.x, y: p.y, z: p.z, vx: d.x, vy: d.y, vz: d.z, gravity: 800, life: rr(0.15, 0.4), size0: 0.7, size1: 0.4, r: 1, g: 0.8, b: 0.45, a0: 1, a1: 0, additive: true, stretch: 0.02 });
      else this.add({ x: p.x, y: p.y, z: p.z, vx: d.x, vy: d.y, vz: d.z, gravity: 800, life: rr(0.5, 0.9), size0: rr(0.8, 1.6), size1: 0.8, r: r * 0.7, g: g * 0.7, b: b * 0.7, a0: 1, a1: 1, tile: Tile.CHIP, spin: rr(-15, 15) });
    }
  }

  /** impacts/fx_flesh_hit_knife_mp (melee) and flesh hits of bolts/knives/axes. */
  blood(p: THREE.Vector3, dir: THREE.Vector3 | null, scale = 1): void {
    for (let i = 0; i < 7; i++) {
      const d = (dir ? dir.clone().multiplyScalar(rr(20, 70)) : new THREE.Vector3()).add(new THREE.Vector3(rr(-30, 30), rr(-10, 40), rr(-30, 30)));
      this.add({ x: p.x, y: p.y, z: p.z, vx: d.x, vy: d.y, vz: d.z, drag: 4, gravity: 120, life: rr(0.35, 0.6), size0: 4 * scale, size1: rr(16, 26) * scale, r: 0.42, g: 0.03, b: 0.02, a0: 0.9, a1: 0, tile: Tile.BLOOD, spin: rr(-2, 2) });
    }
    for (let i = 0; i < 10; i++) {
      const d = (dir ? dir.clone().multiplyScalar(rr(60, 160)) : new THREE.Vector3()).add(new THREE.Vector3(rr(-70, 70), rr(0, 120), rr(-70, 70)));
      this.add({ x: p.x, y: p.y, z: p.z, vx: d.x, vy: d.y, vz: d.z, gravity: 800, life: rr(0.4, 0.7), size0: rr(0.8, 1.6), size1: 0.6, r: 0.35, g: 0.02, b: 0.02, a0: 1, a1: 0.8, tile: Tile.BLOOD, stretch: 0.012 });
    }
  }

  /** mp_hijacked.gsc water splash (trigger_hurt below mapcenter z, SPLASH event) / water impacts. */
  splash(p: THREE.Vector3, scale = 1): void {
    for (let i = 0; i < Math.round(26 * scale) + 4; i++) {
      const d = new THREE.Vector3(rr(-1, 1) * 120, rr(250, 560), rr(-1, 1) * 120).multiplyScalar(Math.sqrt(scale));
      this.add({ x: p.x + rr(-12, 12) * scale, y: p.y, z: p.z + rr(-12, 12) * scale, vx: d.x, vy: d.y, vz: d.z, gravity: 800, life: rr(0.6, 1.1), size0: rr(3, 6) * scale, size1: rr(8, 14) * scale, r: 0.9, g: 0.95, b: 1, a0: 0.8, a1: 0, tile: Tile.SMOKE, stretch: 0.02 });
    }
    for (let i = 0; i < 6; i++) {
      this.add({ x: p.x + rr(-20, 20) * scale, y: p.y + 4, z: p.z + rr(-20, 20) * scale, vx: rr(-40, 40), vy: rr(10, 40), vz: rr(-40, 40), drag: 2, life: rr(1, 1.6), size0: 20 * scale, size1: 70 * scale, r: 0.85, g: 0.9, b: 0.95, a0: 0.5, a1: 0, tile: Tile.SMOKE });
    }
  }

  /** weapon/muzzleflashes/fx_crossbow_base (world muzzle flash of crossbow + ballistic knife). */
  muzzle(p: THREE.Vector3): void {
    this.add({ x: p.x, y: p.y, z: p.z, life: 0.06, size0: 8, size1: 12, r: 1, g: 0.85, b: 0.6, a0: 0.8, a1: 0, additive: true });
  }

  /** weapon/crossbow/fx_trail_crossbow, fx_trail_knife_ballistic, grenade/fx_trail_hatchet. */
  trail(p: THREE.Vector3, kind: 'bolt' | 'knife' | 'hatchet'): void {
    const a = kind === 'hatchet' ? 0.12 : 0.22;
    this.add({ x: p.x, y: p.y, z: p.z, vx: rr(-3, 3), vy: rr(0, 4), vz: rr(-3, 3), life: rr(0.25, 0.4), size0: 1.5, size1: kind === 'bolt' ? 7 : 5, r: 0.85, g: 0.85, b: 0.85, a0: a, a1: 0, tile: Tile.SMOKE });
  }

  /** weapon/crossbow/fx_trail_crossbow_blink_red_os (enemy) / _grn_os (own) — one blink. */
  blink(p: THREE.Vector3, friendly: boolean): void {
    const [r, g, b] = friendly ? [0.3, 1, 0.3] : [1, 0.12, 0.08];
    this.add({ x: p.x, y: p.y, z: p.z, life: 0.08, size0: 9, size1: 7, r, g, b, a0: 1, a1: 0.6, additive: true });
  }

  update(dt: number, camera: THREE.Camera): void {
    const ps = this.parts;
    let w = 0;
    for (let i = 0; i < ps.length; i++) {
      const p = ps[i];
      p.age += dt;
      if (p.age >= p.delay + p.life) continue;
      if (p.age > p.delay) {
        if (p.drag > 0) { const k = Math.exp(-p.drag * dt); p.vx *= k; p.vy *= k; p.vz *= k; }
        p.vy += (p.lift - p.gravity) * dt;
        p.x += p.vx * dt; p.y += p.vy * dt; p.z += p.vz * dt;
        p.rot += p.spin * dt;
      }
      ps[w++] = p;
    }
    ps.length = w;
    for (let i = this.lights.length - 1; i >= 0; i--) { this.lights[i].age += dt; if (this.lights[i].age >= this.lights[i].life) this.lights.splice(i, 1); }
    // back-to-front (particles still waiting on their delay are skipped)
    const cp = camera.position;
    const order = this.order;
    order.length = 0;
    for (let i = 0; i < ps.length; i++) {
      if (ps[i].age < ps[i].delay) continue;
      const dx = ps[i].x - cp.x, dy = ps[i].y - cp.y, dz = ps[i].z - cp.z;
      this.depth[i] = dx * dx + dy * dy + dz * dz;
      order.push(i);
    }
    order.sort((a, b) => this.depth[b] - this.depth[a]);
    const P = this.aPos.array as Float32Array, C = this.aCol.array as Float32Array, M = this.aMisc.array as Float32Array, V = this.aVel.array as Float32Array;
    for (let k = 0; k < order.length; k++) {
      const p = ps[order[k]];
      const t = Math.min(1, (p.age - p.delay) / p.life);
      P[k * 4] = p.x; P[k * 4 + 1] = p.y; P[k * 4 + 2] = p.z; P[k * 4 + 3] = p.size0 + (p.size1 - p.size0) * t;
      C[k * 4] = p.r + (p.r1 - p.r) * t; C[k * 4 + 1] = p.g + (p.g1 - p.g) * t; C[k * 4 + 2] = p.b + (p.b1 - p.b) * t;
      C[k * 4 + 3] = particleAlpha(t, p.a0, p.a1, p.fadeIn);
      M[k * 4] = p.rot; M[k * 4 + 1] = p.tile; M[k * 4 + 2] = p.stretch; M[k * 4 + 3] = p.additive ? 1 : 0;
      V[k * 3] = p.vx; V[k * 3 + 1] = p.vy; V[k * 3 + 2] = p.vz;
    }
    this.geo.instanceCount = order.length;
    for (const a of [this.aPos, this.aCol, this.aMisc, this.aVel]) {
      a.clearUpdateRanges();
      a.addUpdateRange(0, order.length * a.itemSize);
      a.needsUpdate = true;
    }
    this.mesh.visible = order.length > 0;
  }

  dispose(): void {
    this.geo.dispose();
    (this.mesh.material as THREE.ShaderMaterial).dispose();
  }
}
